import { AutocompleteInput } from '@src/shared/ui/inputs';

type PanelFilterProps<T extends string> = {
  /** Names the control for assistive tech: the header shows no label. */
  label: string;
  value: T;
  options: { label: string; value: T }[];
  /**
   * What an emptied input means. One option is always chosen, so the clear
   * button is hidden -- but the keyboard can still empty the field, and it
   * has to land somewhere.
   */
  fallback: T;
  onChange: (value: T) => void;
};

/**
 * The picker the tasks panel puts in its header.
 *
 * Shared rather than written per filter because the two that use it sit in the
 * same slot and are never seen together: the panel offers the due filter or
 * the ownership one depending on whether it is scoped to a section, so any
 * difference in how they look would read as the panel changing shape.
 */
export default function PanelFilter<T extends string>({
  label,
  value,
  options,
  fallback,
  onChange,
}: PanelFilterProps<T>) {
  return (
    <AutocompleteInput
      aria-label={label}
      value={value}
      options={options}
      onChange={(nextValue) => onChange((nextValue as T | null) ?? fallback)}
      placeholder={
        options.find((option) => option.value === fallback)?.label ?? ''
      }
      sx={{
        '& .MuiTextField-root': {
          margin: 0,
        },
        // One of the options is always chosen, so there is no cleared state to
        // offer. `AutocompleteInput` pins MUI's DisableClearable to false, so
        // the button has to go from the style rather than the prop.
        '& .MuiAutocomplete-clearIndicator': {
          display: 'none',
        },
      }}
    />
  );
}
